import { Box, Flex, Stack, StyleProps, useMediaQuery } from "@chakra-ui/react";
import { ProductDetails } from "@the-volte/svc-core-sdk/lib/fragments/gqlTypes/ProductDetails";
import { ISelectedProductInput } from "@the-volte/svc-core-sdk/lib/helpers";
import { parseISO } from "date-fns";
import dayjs from "dayjs";
import { isEmpty, some } from "lodash";
import Head from "next/head";
import Router, { useRouter } from "next/router";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Media from "react-media";
import { generatePath } from "react-router";

import { Button } from "@components/atoms";
import {
  ProductDescription,
  ProductLender,
  ProductYMAL,
} from "@components/molecules";
import {
  AddToCartSection,
  AddToCartSectionDesignB,
  ContactDetails,
  ProductGallery,
} from "@components/organisms";
import * as S from "@components/organisms/AddToCartSection/styles";
import { ProductRecommendations } from "@components/organisms/ProductRecommendations";
import { SamedayPickupDrawer } from "@components/organisms/SamedayPickup/Drawer";
import { SamedayPickupMessageCard } from "@components/organisms/SamedayPickup/MessageCard";
import { SamedayPickupSetLocationCard } from "@components/organisms/SamedayPickup/SetLocationCard";
import { useSamedayPickup } from "@components/organisms/SamedayPickup/useSamedayPickup";
import { paths } from "@paths";
import { defaultTheme } from "@styles";
import { breakpoints } from "@styles/constants";

import { Breadcrumbs } from "../../components/legacy";
import { structuredData } from "../../core/SEO/Product/structuredData";
import { mediumScreen } from "../../globalStyles/variables";
import GalleryCarousel from "./GalleryCarousel";
import {
  type ProductResourceBookingData,
  useLenderProductsQuery,
  useProductResourceBooking,
  useSimilarProductsQuery,
} from "./queries";
import { IProps } from "./types";

const populateBreadcrumbs = (product?: ProductDetails | null) => [
  {
    link: generatePath(
      product?.category?.slug === "dresses"
        ? paths.dresses
        : product?.category?.slug === "clothing"
        ? paths.clothing
        : paths.category,
      { slug: product?.category?.slug }
    ),
    value: product?.category?.name || "",
  },
  {
    link: generatePath(paths.product, { slug: product?.slug }),
    value: product?.name || "",
  },
];

const InfoBox = ({
  children,
  fixed,
}: {
  children: JSX.Element;
  fixed?: boolean;
}) => {
  const fixedStyling: StyleProps = fixed
    ? {
        top: 0,
        position: "sticky",
        width: {
          base: "auto",
          lg: `calc(${defaultTheme.sizes.container.lg} * 30 / 100)`,
        },
      }
    : {};
  return (
    <Box
      flexGrow={1}
      position="relative"
      width={{ base: "100%", lg: "50%" }}
      paddingLeft={{ lg: fixed ? 0 : "60px" }}
      {...fixedStyling}
    >
      {children}
    </Box>
  );
};

const Page: React.FC<IProps> = ({
  add,
  product,
  items,

  editLink,
  user,
  toggleWishlist,
  isInWishlist,
  removeItem,
  loaded = false,
  showRecommendations,
}) => {
  const [clientFetchBookings, setClientFetchBookings] = useState(false);
  const { push, query } = useRouter();
  const [isMobile] = useMediaQuery(`(max-width: ${breakpoints.md})`);
  const productGalleryRef =
    React.useRef() as React.MutableRefObject<HTMLInputElement>;

  const { data } = useSimilarProductsQuery(product, showRecommendations);

  const {
    data: productBookingsData,
    loading: productBookingsLoading,
    refetch: refetchProductBookings,
  } = useProductResourceBooking(
    typeof query.slug === "string" ? query.slug : ""
  );

  const initialProductBookings =
    product?.resource as unknown as ProductResourceBookingData["product"]["resource"];
  const clientProductBookings = productBookingsData?.product?.resource;

  const bookingsToUse =
    productBookingsLoading && !clientFetchBookings
      ? { type: "ssr", data: initialProductBookings }
      : { type: "client", data: clientProductBookings };

  const bookedDates = React.useMemo(
    () =>
      bookingsToUse?.data?.bookings?.reduce(
        (acc1, booking) => [
          ...acc1,
          ...(booking?.dates?.reduce(
            (acc2, acc3) => [...acc2, parseISO(acc3?.date)],
            []
          ) || []),
        ],
        [] as Date[]
      ) || [],
    [bookingsToUse?.data?.bookings]
  );

  const lenderProductsData = useLenderProductsQuery(
    product?.lender?.slug || ""
  );
  const numberOfProducts = lenderProductsData.data?.products?.totalCount || 0;

  const [variantId, setVariantId] = React.useState(
    product?.defaultVariant?.id || ""
  );

  useEffect(() => {
    setVariantId(product?.defaultVariant?.id || "");
  }, [product?.id]);

  const images = React.useMemo(() => {
    if (product?.variants && variantId) {
      const variant = product.variants.find((v) => v?.id === variantId);

      if (variant?.images && variant.images.length > 0) {
        return variant?.images;
      }
    }

    return product?.images;
  }, [product?.variants, variantId]);

  const autocompleteInputRef = useRef<HTMLInputElement>(null);

  const {
    canPickupToday,
    distance,
    errorMessage,
    handleUseCurrentLocation,
    isDrawerOpen,
    isSamedayEligible,
    loading,
    reset,
    setIsDrawerOpen,
    isLoaded,
  } = useSamedayPickup({
    productWarehouseLocation: product?.location || "",
    inputElement: autocompleteInputRef.current,
  });

  const onSubmit = async (params: ISelectedProductInput) => {
    add({
      ...params,
      resourceId: product?.resource?.id,
      isSamedayEligible: isSamedayEligible && isSamedayOrNextdayValid,
    });

    // If user is not logged in then redirect to login before proceeding to checkout
    push(user ? paths.checkout : paths.login);
  };

  const hasPurchaseVariant = product?.variants?.find(
    (productVariant) => productVariant?.name === "Purchase"
  );
  const isPurchaseType =
    query?.price === "purchase" && !isEmpty(hasPurchaseVariant);

  const hasNoVariants = isEmpty(product?.variants);
  const isAvailableForRent = hasNoVariants
    ? false
    : some(
        product?.variants,
        (variant) => !!variant?.pricing?.price?.gross?.amount
      );

  const removeToCartHandler = async (productVariantId: string) => {
    const removeItemReq = await removeItem(productVariantId);

    await refetchProductBookings();

    setClientFetchBookings(true);

    return removeItemReq;
  };

  const designer = product?.attributes?.find(
    (attr) => attr.attribute.name === "Designer"
  )?.values?.[0]?.name;

  const designerSlug = product?.attributes?.find(
    (attr) => attr.attribute.name === "Designer"
  )?.values?.[0]?.slug;

  const rentalDays = useMemo<number | null | undefined>(
    () => product?.variants?.find((pv) => pv?.id === variantId)?.rentalDays,
    [product?.variants, variantId]
  );

  const isSamedayOrNextdayValid = useMemo(() => {
    if (!bookedDates) return true;

    const today = dayjs().startOf("day");
    const tomorrow = dayjs().add(1, "day").startOf("day");

    const bookedDatesSet = new Set(
      bookedDates.map((date) =>
        dayjs(date).startOf("day").toDate().toLocaleDateString()
      )
    );

    const areDaysAvailable = (startDate: dayjs.Dayjs) => {
      const preparationDays = product?.resource?.preparationDays || 0;
      const datesToCheck = Array.from(
        { length: (rentalDays || 4) + preparationDays },
        (_, index) => startDate.add(index, "day").toDate().toLocaleDateString()
      );

      return datesToCheck.every((date) => !bookedDatesSet.has(date));
    };

    if (canPickupToday) {
      // Check if the product is available for rent for 4 days starting today
      return areDaysAvailable(today);
    }

    // Check if the product is available for rent for 4 days starting tomorrow
    return areDaysAvailable(tomorrow);
  }, [bookedDates, canPickupToday, items, rentalDays]);

  const shouldDisplaySamedayPickup = useMemo(() => {
    const samedayPickupEnabled = product?.shippingMethods?.find((method) =>
      method?.name?.toLowerCase()?.includes("same day")
    );

    // Same day pickup will only be displayed when
    // lender has enabled same day or next day pickup in his/her warehouse,
    // and if the product is not yet booked today or tomorrow
    return (
      !!product?.location && !!samedayPickupEnabled && isSamedayOrNextdayValid
    );
  }, [isSamedayOrNextdayValid, product]);

  const addToCartSection = ({ mobile }: { mobile: boolean }) =>
    isPurchaseType ? (
      <AddToCartSectionDesignB
        items={items}
        bookedDates={bookedDates}
        name={product?.name || ""}
        designer={designer || ""}
        designerSlug={designerSlug || ""}
        product={product}
        productVariants={product?.variants}
        productPricing={product?.pricing}
        productAttributes={product?.attributes}
        variantId={variantId}
        onSubmit={onSubmit}
        isAvailableForPurchase={product?.isAvailableForPurchase}
        productBookingsLoading={clientFetchBookings && productBookingsLoading}
        loaded={loaded}
        isSamedayEligible={isSamedayEligible && shouldDisplaySamedayPickup}
        canPickupToday={canPickupToday}
        onRentClick={() => {
          Router.push(generatePath(paths.product, { slug: product?.slug }));
        }}
      />
    ) : (
      <AddToCartSection
        items={items}
        bookedDates={bookedDates}
        name={product?.name || ""}
        designer={designer || ""}
        designerSlug={designerSlug || ""}
        product={product}
        productVariants={product?.variants}
        productPricing={product?.pricing}
        productSeoDescription={product?.seoDescription}
        productAttributes={product?.attributes}
        setVariantId={setVariantId}
        variantId={variantId}
        onSubmit={onSubmit}
        isAvailableForPurchase={product?.isAvailableForPurchase}
        availableForPurchase={product?.availableForPurchase}
        mobile={mobile}
        removeItem={removeToCartHandler}
        productBookingsLoading={clientFetchBookings && productBookingsLoading}
        loaded={loaded}
        isAvailableForRent={isAvailableForRent}
        isSamedayEligible={isSamedayEligible && shouldDisplaySamedayPickup}
        canPickupToday={canPickupToday}
      />
    );

  const size = useMemo(() => {
    const sizeAttribute = product?.attributes?.find(
      (attr) => attr.attribute.name === "Size"
    );
    const productSize = sizeAttribute?.values?.[0]?.name || "Not Found";

    return productSize;
  }, [product]);

  useEffect(() => {
    refetchProductBookings();
  }, [items]);

  const handleChangeLocation = () => {
    if (autocompleteInputRef.current) {
      autocompleteInputRef.current.value = "";
    }

    reset();
    setIsDrawerOpen(true);
  };

  return (
    <>
      {product?.location && product?.slug && isLoaded && (
        <SamedayPickupDrawer
          autocompleteInputRef={autocompleteInputRef}
          errorMessage={errorMessage}
          handleUseCurrentLocation={handleUseCurrentLocation}
          isDrawerOpen={isDrawerOpen}
          isSamedayEligible={isSamedayEligible}
          loading={loading}
          reset={reset}
          setIsDrawerOpen={setIsDrawerOpen}
          distance={distance}
          canPickupToday={canPickupToday}
        />
      )}
      <Box backgroundColor={defaultTheme.colors.white}>
        <div className="container">
          <Breadcrumbs breadcrumbs={populateBreadcrumbs(product)} />
        </div>
        <Box
          display="flex"
          marginTop="1rem"
          marginBottom={{ base: "2rem", lg: "50px" }}
        >
          <Box
            py={0}
            px={[0, null, null, "12px"]}
            width="100%"
            maxWidth={{ base: "100vw", lg: defaultTheme.sizes.container.lg }}
            paddingLeft="0.625rem"
            paddingRight="0.625rem"
            margin={{ lg: "0 auto" }}
          >
            <Head>
              <meta name="robots" content="max-image-preview:large" />
            </Head>
            <script
              className="structured-data-list"
              type="application/ld+json"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: structuredData(product, isPurchaseType),
              }}
            />
            <Media query={{ maxWidth: mediumScreen }}>
              {(matches) =>
                matches ? (
                  <Flex flexDirection="column">
                    {editLink}
                    <GalleryCarousel
                      images={images}
                      toggleWishlist={toggleWishlist}
                      isInWishlist={isInWishlist}
                    />
                    {!product?.isDeleted && !showRecommendations && (
                      <Box marginTop="1rem">
                        <InfoBox>{addToCartSection({ mobile: true })}</InfoBox>
                        {shouldDisplaySamedayPickup && (
                          <>
                            {!distance ? (
                              <SamedayPickupSetLocationCard
                                setIsDrawerOpen={setIsDrawerOpen}
                              />
                            ) : (
                              <Stack
                                gap="0.5rem"
                                style={{ margin: 0, padding: 0 }}
                              >
                                <SamedayPickupMessageCard
                                  isSamedayEligible={isSamedayEligible}
                                  distance={distance}
                                  canPickupToday={canPickupToday}
                                />
                                <Button onClick={handleChangeLocation}>
                                  Change my location
                                </Button>
                              </Stack>
                            )}
                          </>
                        )}
                      </Box>
                    )}
                    {showRecommendations && (
                      <Box flex={1} marginTop="1rem">
                        <S.ProductNameHeader data-test="productName">
                          {product?.name}
                        </S.ProductNameHeader>
                        <S.ProductSizeHeader data-test="productSize">
                          {`Size ${size}`}
                        </S.ProductSizeHeader>
                        <ProductRecommendations
                          recommendations={data?.products?.edges}
                          category={product?.category?.name?.toLowerCase()}
                        />
                      </Box>
                    )}
                  </Flex>
                ) : (
                  <Flex sx={{ gap: showRecommendations ? "1.5rem" : "" }}>
                    <Box
                      className="gallery"
                      ref={productGalleryRef}
                      flexGrow={2}
                      padding="0"
                      boxSizing="border-box"
                      width="100%"
                      flex={showRecommendations ? 1.5 : ""}
                    >
                      <ProductGallery
                        images={images}
                        editLink={editLink}
                        toggleWishlist={toggleWishlist}
                        isInWishlist={isInWishlist}
                      />
                    </Box>
                    {!product?.isDeleted && !showRecommendations && (
                      <Stack>
                        <InfoBox>
                          <InfoBox fixed>
                            <Stack gap="1rem">
                              {addToCartSection({ mobile: false })}
                              {shouldDisplaySamedayPickup && (
                                <>
                                  {!distance ? (
                                    <SamedayPickupSetLocationCard
                                      setIsDrawerOpen={setIsDrawerOpen}
                                    />
                                  ) : (
                                    <Stack
                                      gap="0.5rem"
                                      style={{ margin: 0, padding: 0 }}
                                    >
                                      <SamedayPickupMessageCard
                                        isSamedayEligible={isSamedayEligible}
                                        distance={distance}
                                        canPickupToday={canPickupToday}
                                      />
                                      <Button onClick={handleChangeLocation}>
                                        Change my location
                                      </Button>
                                    </Stack>
                                  )}
                                </>
                              )}
                            </Stack>
                          </InfoBox>
                        </InfoBox>
                      </Stack>
                    )}
                    {showRecommendations && (
                      <Box flex={1}>
                        <S.ProductNameHeader data-test="productName">
                          {product?.name}
                        </S.ProductNameHeader>
                        {size && (
                          <S.ProductSizeHeader data-test="productSize">
                            {`Size ${size}`}
                          </S.ProductSizeHeader>
                        )}
                        <ProductRecommendations
                          recommendations={data?.products?.edges}
                          category={product?.category?.name?.toLowerCase()}
                        />
                      </Box>
                    )}
                  </Flex>
                )
              }
            </Media>
          </Box>
        </Box>
        <div className="container">
          <Box width="100%" minHeight="10rem">
            {product && (
              <ProductDescription
                description={product.description}
                attributes={product.attributes}
                categorySlug={product.category?.slug}
                product={product}
              />
            )}
          </Box>
        </div>

        {!!product?.lender && (
          <ProductLender
            lender={product.lender}
            numberOfProducts={numberOfProducts}
          />
        )}
        <Box
          padding={isMobile ? "0 0.93rem" : "0 8rem"}
          marginBottom={showRecommendations ? "3rem" : ""}
        >
          <ContactDetails />
        </Box>
        {data?.products?.edges?.length && !showRecommendations && (
          <ProductYMAL products={data.products.edges} />
        )}
      </Box>
    </>
  );
};

export default Page;
